import { useState } from "react";

import { type DealFinancialReturnsResponse } from "@vega-api/generated/types/dealFinancialReturnsResponse";
import { MetricCard } from "@vega-core/components/MetricCard/MetricCard";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";

import {
  formatDpi,
  formatIrr,
  formatIrrDelta,
  formatMoic,
  formatPeriodLabel,
  formatRatioDelta,
  formatTvpi,
  getMetricLabel,
  getMetricTrend,
  MetricKey,
  TimeRange,
} from "../../utils/formatFinancialReturns";

import { FinancialReturnsTrendChart } from "./FinancialReturnsTrendChart";
import { ReportSectionMessage } from "./ReportSectionMessage";

type KpiTileConfig = {
  key: MetricKey;
  getValue: (data: DealFinancialReturnsResponse) => number | undefined;
  getDelta: (data: DealFinancialReturnsResponse) => number | undefined;
  formatValue: (value: number | undefined) => string;
  formatDelta: (value: number) => string;
  shouldShowDelta?: (data: DealFinancialReturnsResponse) => boolean;
};

const LOADING_PLACEHOLDER_KEYS: MetricKey[] = [
  MetricKey.Moic,
  MetricKey.Irr,
  MetricKey.Dpi,
  MetricKey.Tvpi,
];

function formatDpiValue(value: number | null | undefined): string {
  if (value == null) return EMPTY_CELL_VALUE;

  if (value === 0) return m.coInvestments_report_dpi_noDistributions();

  return formatDpi(value);
}

function formatDeltaVsPrevious({
  delta,
  previousPeriodDate,
  formatDelta,
}: {
  delta: number | null | undefined;
  previousPeriodDate: string | null | undefined;
  formatDelta: (value: number) => string;
}): string | undefined {
  if (delta == null || previousPeriodDate == null) return undefined;

  return m.coInvestments_report_vsPeriod({
    delta: formatDelta(delta),
    period: formatPeriodLabel(previousPeriodDate),
  });
}

const KPI_TILES: KpiTileConfig[] = [
  {
    key: MetricKey.Moic,
    getValue: (data) => data.latestSnapshot?.moicGross,
    getDelta: (data) => data.deltaVsPrevious?.moicGrossDelta,
    formatValue: formatMoic,
    formatDelta: formatRatioDelta,
  },
  {
    key: MetricKey.Irr,
    getValue: (data) => data.latestSnapshot?.irrNet,
    getDelta: (data) => data.deltaVsPrevious?.irrNetDelta,
    formatValue: formatIrr,
    formatDelta: formatIrrDelta,
  },
  {
    key: MetricKey.Dpi,
    getValue: (data) => data.latestSnapshot?.dpi,
    getDelta: (data) => data.deltaVsPrevious?.dpiDelta,
    formatValue: formatDpiValue,
    formatDelta: formatRatioDelta,
    shouldShowDelta: (data) =>
      data.latestSnapshot?.dpi != null && data.latestSnapshot.dpi !== 0,
  },
  {
    key: MetricKey.Tvpi,
    getValue: (data) => data.latestSnapshot?.tvpi,
    getDelta: (data) => data.deltaVsPrevious?.tvpiDelta,
    formatValue: formatTvpi,
    formatDelta: formatRatioDelta,
  },
];

function getKpiDeltaText(
  tile: KpiTileConfig,
  data: DealFinancialReturnsResponse,
): string | undefined {
  if (tile.shouldShowDelta?.(data) === false) return undefined;

  return formatDeltaVsPrevious({
    delta: tile.getDelta(data),
    previousPeriodDate: data.deltaVsPrevious?.previousPeriodDate,
    formatDelta: tile.formatDelta,
  });
}

function FinancialReturnsLoadingGrid({
  timeRange,
  onTimeRangeChange,
}: {
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
}) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {LOADING_PLACEHOLDER_KEYS.map((key) => (
          <MetricCard
            dataTestId={`financial-returns-loading-${key}`}
            isLoading
            key={key}
            title=""
            value=""
          />
        ))}
      </div>
      <FinancialReturnsTrendChart
        activeMetric={MetricKey.Moic}
        isLoading
        onTimeRangeChange={onTimeRangeChange}
        points={[]}
        timeRange={timeRange}
      />
    </>
  );
}

function KpiTile({
  tile,
  data,
  activeMetric,
  onMetricSelect,
}: {
  tile: KpiTileConfig;
  data: DealFinancialReturnsResponse;
  activeMetric: MetricKey;
  onMetricSelect: (metric: MetricKey) => void;
}) {
  const value = tile.getValue(data);
  const deltaText = getKpiDeltaText(tile, data);

  return (
    <button
      className="text-left rounded-lg focus-outline-themed"
      data-testid={`financial-returns-tile-${tile.key}`}
      onClick={() => onMetricSelect(tile.key)}
      type="button"
    >
      <MetricCard
        dataTestId={`financial-returns-metric-${tile.key}`}
        hasNoData={value == null}
        isSelected={activeMetric === tile.key}
        periodContent={
          deltaText ? (
            <p className="body-small-regular text-text/secondary">
              {deltaText}
            </p>
          ) : undefined
        }
        title={getMetricLabel(tile.key)}
        value={tile.formatValue(value)}
      />
    </button>
  );
}

function FinancialReturnsMetricsGrid({
  activeMetric,
  data,
  onMetricSelect,
}: {
  activeMetric: MetricKey;
  data: DealFinancialReturnsResponse;
  onMetricSelect: (metric: MetricKey) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {KPI_TILES.map((tile) => (
        <KpiTile
          activeMetric={activeMetric}
          data={data}
          key={tile.key}
          onMetricSelect={onMetricSelect}
          tile={tile}
        />
      ))}
    </div>
  );
}

function FinancialReturnsContent({
  activeMetric,
  data,
  isError,
  isPending,
  onMetricSelect,
  onRetry,
  onTimeRangeChange,
  timeRange,
}: {
  activeMetric: MetricKey;
  data: DealFinancialReturnsResponse | undefined;
  isError: boolean;
  isPending: boolean;
  onMetricSelect: (metric: MetricKey) => void;
  onRetry: () => void;
  onTimeRangeChange: (range: TimeRange) => void;
  timeRange: TimeRange;
}) {
  if (isPending)
    return (
      <FinancialReturnsLoadingGrid
        onTimeRangeChange={onTimeRangeChange}
        timeRange={timeRange}
      />
    );

  if (isError) {
    return (
      <ReportSectionMessage
        dataTestId="financial-returns-error"
        message={m.common_error_somethingWentWrong()}
        onRetry={onRetry}
      />
    );
  }

  if (!data || !data.latestSnapshot) {
    return (
      <ReportSectionMessage
        dataTestId="financial-returns-empty"
        message={m.coInvestments_report_financialReturns_noData()}
      />
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <FinancialReturnsMetricsGrid
        activeMetric={activeMetric}
        data={data}
        onMetricSelect={onMetricSelect}
      />

      <FinancialReturnsTrendChart
        activeMetric={activeMetric}
        onTimeRangeChange={onTimeRangeChange}
        points={getMetricTrend(data, activeMetric)}
        timeRange={timeRange}
      />
    </div>
  );
}

export function FinancialReturnsSection({
  data,
  isError,
  isPending,
  onRetry,
}: {
  data: DealFinancialReturnsResponse | undefined;
  isError: boolean;
  isPending: boolean;
  onRetry: () => void;
}) {
  const [activeMetric, setActiveMetric] = useState<MetricKey>(MetricKey.Moic);
  const [timeRange, setTimeRange] = useState<TimeRange>(TimeRange.All);

  const latestPeriodLabel = data?.latestSnapshot?.periodDate
    ? formatPeriodLabel(data.latestSnapshot.periodDate)
    : undefined;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="heading-5">
          {m.coInvestments_report_financialReturns_latest()}
        </h2>
        {latestPeriodLabel ? (
          <p className="body-small-regular text-text/secondary">
            {m.coInvestments_report_financialReturns_asOf({
              period: latestPeriodLabel,
            })}
          </p>
        ) : null}
      </div>

      <FinancialReturnsContent
        activeMetric={activeMetric}
        data={data}
        isError={isError}
        isPending={isPending}
        onMetricSelect={setActiveMetric}
        onRetry={onRetry}
        onTimeRangeChange={setTimeRange}
        timeRange={timeRange}
      />
    </div>
  );
}
